import { useErrorNotificationApi } from '@equifinance/shared';
import { Account, TransitionError } from '../contexts/account/Account';
import { Case } from '../contexts/cases';

const apiPath = `${process.env.REACT_APP_ORIGINATION_API_PATH}/`;

const apiUrl = (path: string) => `${apiPath}${path}`;

export const OtherReason = 'Other';

export type StaticDataItem = {
  key: string;
  label: string;
  index: number;
}

export type StaticData = {
  industries: StaticDataItem[];
  jobTitles: StaticDataItem[];
  nationalities: StaticDataItem[];
  propertyTypes: StaticDataItem[];
  lendingRegions: StaticDataItem[];
  countries: StaticDataItem[];
  ukCountries: StaticDataItem[];
  accountancyQualifications: StaticDataItem[];
}

export type WithdrawalReason = {
  key: string;
  value: string;
}

export type WithdrawReasonType = {
  reason: string,
  otherDescription?: string | null,
}

export type TransitionType = {
  applicationId: string
}

export enum TransitionStatusType {
  TransitionNotEligible = 'TransitionNotEligible',
  TransitionNotAllowed = 'TransitionNotAllowed',
  TransitionAllowed = 'TransitionAllowed',
}

export type TransitionStatus = {
  transitionLabel: string;
  transitionStatus: TransitionStatusType;
  reason: {
    errors: TransitionError[] | null;
    warnings: TransitionError[] | null;
  } | null,
}

export type TransitionStageContext = {
  hasConsent: boolean,
  readyForServicing: boolean,
}

export type TransitionForward = {
  actionResult: {
    transitionSuccessful: boolean,
    reason: {
      errors: TransitionError[],
      warnings: TransitionError[],
    },
  },
  transitionState: TransitionStatus,
}

export const defaultTransitionStatus: TransitionStatus = {
  transitionLabel: '',
  transitionStatus: TransitionStatusType.TransitionNotEligible,
  reason: null,
};

export type CaseApiType = {
  getAccount: (id: string) => Promise<Account>,
  putAccount: (account: Account, accountId: string) => Promise<Account>,
  postSubmitAccount: (account: Partial<Account>) => Promise<Account>,
  getStaticData: () => Promise<StaticData>,
  getCases: () => Promise<Case[]>,
  postUpdateCase: (caseData: Partial<Case>) => Promise<Case[]>,
  postDeleteCase: (caseId: string) => Promise<void>,
  putWithdrawCase: (caseId: string, withdrawReason: WithdrawReasonType) => Promise<Case | string>,
  putReopenCase: (caseId: string) => Promise<Case | string>,
  getWithdrawalReasons: () => Promise<WithdrawalReason[]>,
  putUpdateBrokerOwner: (ownerId: string, brokerAssignedToId: string, brokerAssignedToName: string | null) => Promise<Case>,
  transition: (applicationId: string, stageContext: TransitionStageContext) => Promise<TransitionForward>,
  getTransitionStatus: (applicationId: string) => Promise<TransitionStatus>,
  postVerifyBankAccount: (applicationId: string) => Promise<Account>,
}

export default function useAccountApi(): CaseApiType {
  const {
    put, post, del, get,
  } = useErrorNotificationApi(apiUrl);

  return {
    getAccount: (id: string): Promise<Account> => get(`ApplicationForm/application-form/${id}`),
    putAccount: (account: Account, accountId: string) => put(`ApplicationForm/application-form/${accountId}`, account),
    postSubmitAccount: (account: Partial<Account>) => post('ApplicationForm/application-form', account),
    getStaticData: (): Promise<StaticData> => get('staticdata/case-details').then((response) => ({ ...response, ukCountries: response?.unitedKingdomAndChannelIslands })),
    getCases: (): Promise<Case[]> => get('CaseManagement/cases'),
    postUpdateCase: (caseData: Partial<Case>): Promise<Case[]> => post('CaseManagement/updateCase', caseData),
    postDeleteCase: (caseId: string): Promise<void> => del(`CaseManagement/cases/${caseId}`),
    putWithdrawCase: (caseId: string, withdrawReason: WithdrawReasonType): Promise<Case | string> => put(`CaseManagement/case/${caseId}/broker-withdraw`, withdrawReason),
    putReopenCase: (caseId: string): Promise<Case | string> => put(`CaseManagement/case/${caseId}/broker-reopen`, {}),
    getWithdrawalReasons: (): Promise<WithdrawalReason[]> => get('CaseManagement/static/withdrawal-reasons'),
    putUpdateBrokerOwner: (
      applicationId,
      brokerAssignedToId,
      brokerAssignedToName,
    ) => put(`Casemanagement/case/${applicationId}/update-broker-owner`, {
      brokerAssignedToId,
      brokerAssignedToName,
    }),
    transition: (applicationId: string, stageContext: TransitionStageContext) => post('transition', { applicationId, stageContext }),
    getTransitionStatus: (applicationId: string) => get(`transition/${applicationId}`),
    postVerifyBankAccount: (applicationId: string) => post('searches/id-and-bank-check', { applicationId }),
  };
}
